import { ThemeUIStyleObject } from 'theme-ui';

type Args = {
  baseColor: 'White' | 'Black' | 'Gold' | string;
  variant: 'Outline' | 'Filled' | string;
  size: 'Small' | 'Medium' | 'Large' | string;
  disableDisabledStyle: boolean;
  forwardSx?: ThemeUIStyleObject;
};

const useCTAStyle = ({ baseColor, variant, size, disableDisabledStyle, forwardSx = {} }: Args) => {
  const isWhite = /white/i.test(baseColor);
  const disabledColor = isWhite ? 'UIGray3' : 'cloud';
  const isOutline = /outline/i.test(variant);
  const isMedium = /medium/i.test(size);
  const isLarge = /large/i.test(size);

  const baseColorOpposite = isWhite ? 'black' : 'white';

  const getFillHoverColor = () => {
    switch (baseColor) {
      case 'Gold':
        return 'goldDark';
      case 'White':
        return 'cloud';
      case 'Black':
      default:
        return 'darkGrey';
    }
  };
  const fillHoverColor = getFillHoverColor();
  const disabledStyle = disableDisabledStyle
    ? {}
    : {
        backgroundColor: isOutline ? 'transparent' : disabledColor,
        color: isOutline ? (isWhite ? 'UIGray3' : 'cloud') : baseColorOpposite,
        borderColor: disabledColor,
      };

  const CTAStyle: ThemeUIStyleObject = {
    backgroundColor: isOutline ? 'transparent' : baseColor.toLowerCase(),
    width: isLarge ? '100%' : 'fit-content',
    border: `1px solid`,
    borderColor: baseColor.toLowerCase(),
    borderRadius: '10rem',
    color: isOutline ? baseColor : baseColorOpposite,
    height: isLarge ? '4.8rem' : isMedium ? '4rem' : '2.8rem',
    textTransform: isLarge ? 'uppercase' : 'capitalize',
    fontFamily: 'body',
    fontSize: isLarge ? '1.4rem' : '1.2rem',
    fontWeight: 'medium',
    lineHeight: 1,
    letterSpacing: '-.01em',
    px: '1.6rem',
    py: isMedium ? '1.2rem' : '0.8rem',
    whiteSpace: 'nowrap',
    minHeight: 0,
    minWidth: 0,
    '&:enabled:hover, &:is(a):hover': {
      backgroundColor: isOutline ? baseColor : fillHoverColor,
      color: isWhite ? 'black' : 'white',
      borderColor: isOutline ? baseColor : fillHoverColor,
    },
    '&:disabled': {
      cursor: 'not-allowed',
      ...disabledStyle,
    },
    '&:focus, &:focus-visible, &:focus-within, &:[data-state="focus"]': {
      outlineStyle: 'solid',
      outlineOffset: '0.5rem',
      outlineColor: isWhite ? 'UIGray2' : 'UIGray1',
      outlineWidth: '1px',
    },
    '&:active': {
      backgroundColor: isWhite ? 'UIGray1' : 'UIGray2 ',
      borderColor: isWhite ? 'UIGray1' : 'UIGray2',
      color: isWhite ? 'black' : 'white',
    },
    transition: 'all 0.3s ease-in-out',
    ...forwardSx,
  };

  return CTAStyle;
};

export default useCTAStyle;
